<template>
  <section id="user-details" class="page-section user-details-section">
    <div class="user-details-scroll" role="region" aria-label="User details form">
      <div class="user-details-content">
        <div v-if="isAuthenticated" class="registration-feedback">
          <p class="feedback-line">
            Nice — name your campaign and you're ready to launch.
          </p>
          <div class="form-field campaign-name-inline">
            <label for="campaignNameInline" class="field-label field-label--compact">Campaign Name</label>
            <input
              type="text"
              id="campaignNameInline"
              enterkeyhint="done"
              :value="campaignName"
              @input="handleCampaignNameInput($event)"
              placeholder="e.g. Summer Promo 2026"
              maxlength="120"
            />
            <small class="field-note">Defaults to your name's campaign — edit if you'd like.</small>
          </div>
        </div>

        <div v-else class="registration-feedback">
          <h3>Now Register</h3>
          <p class="feedback-line">You Must Register Below Before You Can Launch A Campaign</p>
          <p v-if="userDetails.email" class="feedback-meta">{{ userDetails.email }}</p>

          <button type="button" class="edit-btn" @click="openRegistrationModal">
            Add details
          </button>
        </div>

      </div>
    </div>

    <div
      v-if="showRegistrationModal"
      class="modal-overlay registration-modal-overlay"
      @click.self="closeRegistrationModal"
    >
      <div class="modal-container registration-modal-container" role="dialog" aria-modal="true" aria-label="Registration form">
        <div class="modal-header">
          <h3>{{ isAuthenticated ? 'Edit your details' : 'Complete registration' }}</h3>
          <button
            type="button"
            class="modal-close-button"
            aria-label="Close registration form"
            @click="closeRegistrationModal"
          >
            ×
          </button>
        </div>

        <div class="modal-body registration-modal-body">
          <div class="details-form">
            <div class="form-row">
              <div class="form-field">
                <label for="campaignName" class="field-label field-label--compact">Campaign Name</label>
                <input
                  type="text"
                  id="campaignName"
                  enterkeyhint="done"
                  :value="campaignName"
                  @input="handleCampaignNameInput($event)"
                  placeholder="e.g. Summer Promo 2026"
                  maxlength="120"
                />
                <small class="field-note">Defaults to your name's campaign — edit if you'd like.</small>
              </div>
            </div>

            <div class="form-row">
              <div class="form-field">
                <label for="firstName" class="field-label field-label--compact">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  autocomplete="given-name"
                  enterkeyhint="done"
                  :value="userDetails.name"
                  @input="handleTextInput('name', $event)"
                  placeholder="Enter your first name"
                />
              </div>
              <div class="form-field">
                <label for="surname" class="field-label field-label--compact">Surname</label>
                <input
                  type="text"
                  id="surname"
                  autocomplete="family-name"
                  enterkeyhint="done"
                  :value="userDetails.surname"
                  @input="handleTextInput('surname', $event)"
                  placeholder="Enter your surname"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-field">
                <label for="email" class="field-label field-label--compact">Email</label>
                <input
                  type="email"
                  id="email"
                  autocomplete="email"
                  enterkeyhint="done"
                  :value="userDetails.email"
                  @input="handleTextInput('email', $event)"
                  placeholder="Enter your email"
                  :disabled="isAuthenticated"
                  :class="{ 'field-disabled': isAuthenticated }"
                />
                <small v-if="isAuthenticated" class="field-note">Email is linked to your account</small>
              </div>

              <div class="form-field phone-field">
                <label for="mobileCountry" class="field-label field-label--compact">Mobile Number</label>
                <div class="phone-inputs">
                  <div class="country-select-wrapper">
                    <select
                      id="mobileCountry"
                      class="country-select"
                      v-model="selectedCountryIso"
                      autocomplete="tel-country-code"
                      aria-label="Country"
                    >
                      <option disabled value="">Country code</option>
                      <option
                        v-for="country in countryOptions"
                        :key="country.iso"
                        :value="country.iso"
                      >
                        {{ country.name }}
                      </option>
                    </select>
                  </div>
                  <input
                    type="tel"
                    id="mobileNumber"
                    autocomplete="tel-national"
                    inputmode="numeric"
                    enterkeyhint="done"
                    :value="userDetails.mobileNumber"
                    @input="handlePhoneNumberInput($event)"
                    @blur="handlePhoneNumberBlur($event)"
                    maxlength="20"
                    placeholder="Mobile"
                  />
                  <small v-if="phoneNumberError" class="field-error">{{ phoneNumberError }}</small>
                </div>
              </div>
            </div>

            <transition name="expand">
              <div v-if="!isAuthenticated" class="password-section-wrapper">
                <div class="form-row">
                  <div class="form-field">
                    <label for="password">Password</label>
                    <div class="password-input">
                      <input
                        :type="getPasswordType(isPasswordVisible)"
                        id="password"
                        autocomplete="new-password"
                        enterkeyhint="done"
                        :value="userDetails.password"
                        @input="handleTextInput('password', $event)"
                        placeholder="At least 8 chars, upper, lower, number"
                        :minlength="PASSWORD_MIN_LENGTH"
                      />
                      <span
                        class="toggle-visibility-btn"
                        role="button"
                        tabindex="0"
                        @click="togglePasswordVisibility('password')"
                        @keydown.enter.prevent="togglePasswordVisibility('password')"
                        @keydown.space.prevent="togglePasswordVisibility('password')"
                        :aria-label="isPasswordVisible ? 'Hide password' : 'Show password'"
                      >
                        <svg
                          v-if="isPasswordVisible"
                          class="toggle-visibility-icon"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            d="M3.5 3.5a1 1 0 0 1 1.4 0l3.1 3.1c1.2-.5 2.5-.8 3.9-.8 5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8 15.2 15.2 0 0 1-4.1 4.2l2.7 2.7a1 1 0 1 1-1.4 1.4l-17-17a1 1 0 0 1 0-1.4Zm7.8 7.8a2.5 2.5 0 0 0 3.4 3.4l-3.4-3.4Zm-2.5-2.5 2.1 2.1a4.5 4.5 0 0 1 6 6l2.2 2.2a13 13 0 0 0 3.7-3.7c-1.4-2.2-4.9-5.6-9.9-5.6-1.4 0-2.8.2-4.1.6Zm4.1 10.3c-5 0-8.5-3.4-9.9-5.6a13 13 0 0 1 3.2-3.5l1.4 1.4a4.5 4.5 0 0 0 5.8 5.8l1.7 1.7c-.7.1-1.5.2-2.2.2Z"
                          />
                        </svg>
                        <svg
                          v-else
                          class="toggle-visibility-icon"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 5.8c5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8c-1.6 2.5-5.8 6.2-11 6.2s-9.4-3.7-11-6.2a1.6 1.6 0 0 1 0-1.8c1.6-2.5 5.8-6.2 11-6.2Zm0 2c-3.9 0-7.3 2.6-8.8 4.6 1.5 2 4.9 4.6 8.8 4.6s7.3-2.6 8.8-4.6c-1.5-2-4.9-5.6-8.8-5.6Zm0 1.7a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
                          />
                        </svg>
                      </span>
                    </div>
                    <small class="hint">Min 8 chars, include upper, lower, and a number.</small>
                  </div>
                  <div class="form-field">
                    <label for="confirmPassword">Confirm Password</label>
                    <div class="password-input">
                      <input
                        :type="getPasswordType(isConfirmPasswordVisible)"
                        id="confirmPassword"
                        autocomplete="new-password"
                        enterkeyhint="done"
                        :value="userDetails.confirmPassword"
                        @input="handleTextInput('confirmPassword', $event)"
                        placeholder="Re-enter your password"
                        :minlength="PASSWORD_MIN_LENGTH"
                      />
                      <span
                        class="toggle-visibility-btn"
                        role="button"
                        tabindex="0"
                        @click="togglePasswordVisibility('confirm')"
                        @keydown.enter.prevent="togglePasswordVisibility('confirm')"
                        @keydown.space.prevent="togglePasswordVisibility('confirm')"
                        :aria-label="isConfirmPasswordVisible ? 'Hide confirm password' : 'Show confirm password'"
                      >
                        <svg
                          v-if="isConfirmPasswordVisible"
                          class="toggle-visibility-icon"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            d="M3.5 3.5a1 1 0 0 1 1.4 0l3.1 3.1c1.2-.5 2.5-.8 3.9-.8 5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8 15.2 15.2 0 0 1-4.1 4.2l2.7 2.7a1 1 0 1 1-1.4 1.4l-17-17a1 1 0 0 1 0-1.4Zm7.8 7.8a2.5 2.5 0 0 0 3.4 3.4l-3.4-3.4Zm-2.5-2.5 2.1 2.1a4.5 4.5 0 0 1 6 6l2.2 2.2a13 13 0 0 0 3.7-3.7c-1.4-2.2-4.9-5.6-9.9-5.6-1.4 0-2.8.2-4.1.6Zm4.1 10.3c-5 0-8.5-3.4-9.9-5.6a13 13 0 0 1 3.2-3.5l1.4 1.4a4.5 4.5 0 0 0 5.8 5.8l1.7 1.7c-.7.1-1.5.2-2.2.2Z"
                          />
                        </svg>
                        <svg
                          v-else
                          class="toggle-visibility-icon"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 5.8c5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8c-1.6 2.5-5.8 6.2-11 6.2s-9.4-3.7-11-6.2a1.6 1.6 0 0 1 0-1.8c1.6-2.5 5.8-6.2 11-6.2Zm0 2c-3.9 0-7.3 2.6-8.8 4.6 1.5 2 4.9 4.6 8.8 4.6s7.3-2.6 8.8-4.6c-1.5-2-4.9-5.6-8.8-5.6Zm0 1.7a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </transition>

            <div class="consent-row">
              <div class="checkbox-field">
                <input
                  type="checkbox"
                  id="terms"
                  :checked="userDetails.consent"
                  @change="handleCheckboxChange('consent', $event)"
                />
                <label for="terms">I consent to the Terms and Conditions</label>
              </div>
              <div class="checkbox-field">
                <input
                  type="checkbox"
                  id="marketing"
                  :checked="userDetails.marketingConsent"
                  @change="handleCheckboxChange('marketingConsent', $event)"
                />
                <label for="marketing">I consent to marketing emails</label>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer registration-modal-footer">
          <button type="button" class="secondary-btn" @click="closeRegistrationModal">Close</button>
          <button type="button" class="primary-btn" @click="handleSaveDetails">Save details</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js'
import { useUIStore } from '@/store/ui.store'
import {
  COUNTRY_DIAL_CODES,
  getCountryByDialCode,
  getCountryByIso
} from '@/utils/countryDialCodes'

export type IndividualDetailsPayload = {
  name: string
  surname: string
  email: string
  mobileCountryCode: string
  mobileNumber: string
  consent: boolean
  marketingConsent: boolean
  password: string
  confirmPassword: string
}

const props = withDefaults(
  defineProps<{
    userDetails: IndividualDetailsPayload
    campaignName: string
    submitSuccess: boolean
    isSubmitting: boolean
    isFormComplete: boolean
    isAuthenticated: boolean
    existingCampaignNames?: readonly string[]
  }>(),
  {
    existingCampaignNames: () => [],
  },
)

const emit = defineEmits<{
  (e: 'update-user-details', payload: IndividualDetailsPayload): void
  (e: 'update-campaign-name', name: string): void
  (e: 'submit'): void
  (e: 'clear-state'): void
  (e: 'scroll-registration-to-bottom'): void
}>()

const PASSWORD_MIN_LENGTH = 8
const PASSWORD_INPUT_TYPE = 'password'
const TEXT_INPUT_TYPE = 'text'
const uiStore = useUIStore()

const isPasswordVisible = ref(false)
const isConfirmPasswordVisible = ref(false)
const showRegistrationModal = ref(false)
const hasSavedDetails = ref(false)
// Tracks whether the current campaign name was auto-generated. Once the
// user types anything manually we flip this false so subsequent name
// changes don't clobber their input.
const isCampaignNameAutoGenerated = ref(true)

/**
 * Build a default campaign name from the user's first name, bumping a
 * counter suffix if that exact name is already in use by another campaign
 * in the same org — `Lukas's Campaign`, `Lukas's Campaign 2`, etc.
 */
const computeDefaultCampaignName = (firstName: string): string => {
  const name = firstName.trim()
  if (!name) return ''
  const base = `${name}'s Campaign`
  const taken = new Set(props.existingCampaignNames.map(n => n.trim()))
  if (!taken.has(base)) return base
  let counter = 2
  while (taken.has(`${base} ${counter}`)) counter += 1
  return `${base} ${counter}`
}

const handleCampaignNameInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  isCampaignNameAutoGenerated.value = false
  emit('update-campaign-name', value)
}

// When the modal is open, keep the campaign name synced to the current
// first name as long as the user hasn't taken over manual editing.
watch(
  [showRegistrationModal, () => props.userDetails.name],
  ([isOpen, firstName]) => {
    if (!isOpen) return
    const currentName = props.campaignName.trim()
    if (currentName && !isCampaignNameAutoGenerated.value) return
    const generated = computeDefaultCampaignName(firstName)
    if (generated && generated !== currentName) {
      emit('update-campaign-name', generated)
      isCampaignNameAutoGenerated.value = true
    }
  },
  { immediate: true },
)

const displayName = computed(() => {
  const first = props.userDetails.name?.trim() || ''
  const last = props.userDetails.surname?.trim() || ''
  return [first, last].filter(Boolean).join(' ') || 'your account'
})

const firstName = computed(() => props.userDetails.name?.trim() || '')

const formattedPhone = computed(() => {
  const code = props.userDetails.mobileCountryCode?.trim() || ''
  const number = props.userDetails.mobileNumber?.trim() || ''
  if (!code || !number) {
    return ''
  }
  return `${code} ${number}`
})

const hasProfileDetails = computed(() => {
  const hasName = props.userDetails.name.trim().length > 0
  const hasSurname = props.userDetails.surname.trim().length > 0
  const hasPhone = props.userDetails.mobileNumber.trim().length > 0
  return hasName && hasSurname && hasPhone
})

const detailsActionLabel = computed(() => {
  return hasProfileDetails.value || hasSavedDetails.value ? 'Edit details' : 'Add details'
})

const openRegistrationModal = () => {
  showRegistrationModal.value = true
}

const closeRegistrationModal = () => {
  showRegistrationModal.value = false
  // Ask FormView to do the scroll — raw window.scrollTo loses a fight with
  // the strict section navigation's scroll-lock on iOS, so we route through
  // FormView's transition machinery (same plumbing the Next button uses).
  emit('scroll-registration-to-bottom')
}

const handleSaveDetails = () => {
  hasSavedDetails.value = true
  closeRegistrationModal()
}

const getPasswordType = (isVisible: boolean) => (isVisible ? TEXT_INPUT_TYPE : PASSWORD_INPUT_TYPE)

const togglePasswordVisibility = (field: 'password' | 'confirm') => {
  if (field === 'password') {
    isPasswordVisible.value = !isPasswordVisible.value
    return
  }
  isConfirmPasswordVisible.value = !isConfirmPasswordVisible.value
}

const updateUserDetails = (changes: Partial<IndividualDetailsPayload>) => {
  emit('update-user-details', { ...props.userDetails, ...changes })
}

const handleTextInput = (
  field: keyof Pick<IndividualDetailsPayload, 'name' | 'surname' | 'email' | 'password' | 'confirmPassword'>,
  event: Event
) => {
  const value = (event.target as HTMLInputElement).value
  updateUserDetails({ [field]: value } as Partial<IndividualDetailsPayload>)
}

const countryOptions = COUNTRY_DIAL_CODES
const SOUTH_AFRICA_ISO = 'ZA'
const SOUTH_AFRICA_DIAL_CODE = '+27'

const selectedCountry = computed(() => {
  return getCountryByDialCode(props.userDetails.mobileCountryCode) ?? getCountryByIso(SOUTH_AFRICA_ISO)
})

const selectedCountryIso = computed({
  get: () => selectedCountry.value?.iso ?? SOUTH_AFRICA_ISO,
  set: (iso: string) => {
    const nextCountry = getCountryByIso(iso) ?? getCountryByIso(SOUTH_AFRICA_ISO)
    if (!nextCountry) {
      return
    }
    updateUserDetails({ mobileCountryCode: nextCountry.dialCode })
  }
})

const phoneNumberError = ref('')

/**
 * Light on-input normalisation — strip non-digits only. We keep the user's
 * typing experience responsive and defer heavy country-code detection to
 * the blur handler, so an in-flight number doesn't get rewritten mid-type.
 *
 * Leading zeros are NOT stripped here (the blur-time libphonenumber pass
 * handles SA's "0123..." local prefix correctly).
 */
const lightNormalize = (raw: string) => raw.replace(/\D/g, '').slice(0, 20)

/**
 * On-blur (or before submit) — use libphonenumber-js to parse the raw input
 * against the selected country. Handles the common user-error cases:
 *
 *   - "0123456789"      (SA local format)       → "123456789"
 *   - "+27123456789"    (full E.164)            → "123456789"
 *   - "27123456789"     (country code, no +)    → "123456789"  ← Google autofill
 *   - "123456789"       (already national)      → "123456789"
 *   - "(+27) 12 345 67" (fancy formatting)      → "12345678"
 *
 * If the parsed number also carries a country code that differs from the
 * dropdown, we sync the dropdown to match so "state follows what the user
 * actually typed" (e.g. pasting "+44..." with ZA selected switches to UK).
 */
const aggressiveNormalize = (raw: string): { national: string; iso?: string; error?: string } => {
  const trimmed = raw.trim()
  if (!trimmed) return { national: '' }

  const currentCountry = selectedCountry.value
  const iso = currentCountry?.iso as CountryCode | undefined

  // parsePhoneNumberFromString accepts both E.164 (+27...) and national
  // forms (using the defaultCountry hint). It's tolerant of spaces, dashes,
  // parens, and missing '+' prefixes on country codes.
  const parsed = parsePhoneNumberFromString(trimmed, iso)

  if (parsed && parsed.isValid()) {
    return {
      national: parsed.nationalNumber,
      iso: parsed.country,
    }
  }

  // Fallback: strip junk and leading zeros like the old path. Lets the
  // user save a "mostly valid" number without blocking submission on
  // parser strictness (we do server-side normalisation too).
  const digitsOnly = trimmed.replace(/\D/g, '').replace(/^0+/, '')
  return {
    national: digitsOnly.slice(0, 15),
    error: digitsOnly.length >= 7 ? undefined : 'Please enter a valid phone number',
  }
}

const handlePhoneNumberInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  const normalized = lightNormalize(input.value)
  input.value = normalized
  phoneNumberError.value = ''
  updateUserDetails({ mobileNumber: normalized })
}

const handlePhoneNumberBlur = (event: Event) => {
  const input = event.target as HTMLInputElement
  const { national, iso, error } = aggressiveNormalize(input.value)

  input.value = national
  phoneNumberError.value = error ?? ''

  const patch: Partial<IndividualDetailsPayload> = { mobileNumber: national }

  // If libphonenumber detected a different country than the dropdown,
  // follow the user's input (e.g. they pasted +44 with ZA selected).
  if (iso && iso !== selectedCountry.value?.iso) {
    const matched = getCountryByIso(iso)
    if (matched) {
      patch.mobileCountryCode = matched.dialCode
    }
  }

  updateUserDetails(patch)
}

const handleCheckboxChange = (
  field: keyof Pick<IndividualDetailsPayload, 'consent' | 'marketingConsent'>,
  event: Event
) => {
  const checked = (event.target as HTMLInputElement).checked
  updateUserDetails({ [field]: checked } as Partial<IndividualDetailsPayload>)
}

watch(
  () => props.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      if (!props.userDetails.consent) {
        updateUserDetails({ consent: true })
      }
      showRegistrationModal.value = false
    }
  },
  { immediate: true }
)

watch(showRegistrationModal, (isOpen) => {
  if (isOpen) {
    uiStore.openModal()
  } else {
    uiStore.closeModal()
  }
})

onMounted(() => {
  if (!props.userDetails.mobileCountryCode) {
    updateUserDetails({ mobileCountryCode: SOUTH_AFRICA_DIAL_CODE })
  }
})

onBeforeUnmount(() => {
  if (showRegistrationModal.value) {
    uiStore.closeModal()
  }
})
</script>

<style scoped>
.user-details-section {
  justify-content: flex-start;
  align-items: stretch;
  /* Matches the canonical section padding used by other steps so the
     register block sits clear of the fixed header. */
  padding-top: var(--section-padding-block-start);
}

.user-details-scroll {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
}

.user-details-content {
  width: 100%;
  max-width: var(--base-layout-form-panel-max-width);
  margin-inline: auto;
}

.registration-feedback {
  background-color: var(--color-background-soft);
  border-radius: var(--base-layout-form-panel-border-radius);
  border: 1px solid var(--color-border);
  padding: var(--base-layout-panel-padding);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.registration-feedback h3 {
  margin: 0 0 var(--base-layout-spacing-md);
  color: var(--color-heading);
  font-size: var(--base-layout-section-heading-h4-size);
}

.campaign-name-field {
  margin-top: clamp(14px, 2vw, 24px);
  display: flex;
  flex-direction: column;
}

.campaign-name-field input {
  padding: var(--base-layout-form-input-padding);
  border: var(--base-layout-form-input-border-width) solid var(--color-border);
  border-radius: var(--base-layout-form-input-border-radius);
  background-color: var(--color-background-mute);
  color: var(--color-text);
  font-size: var(--base-layout-form-input-font-size);
  transition: all var(--base-layout-transition-normal);
}

.campaign-name-field input::placeholder {
  color: var(--color-text);
  opacity: 0.4;
}

.campaign-name-field input:focus {
  outline: none;
  border-color: var(--vt-c-accent);
  box-shadow: 0 0 0 var(--base-layout-form-input-focus-ring-size) var(--base-layout-form-input-focus-ring-color);
  background-color: var(--color-background-secondary);
}

.feedback-line {
  margin: 0;
  color: var(--color-text);
}

.campaign-name-inline {
  margin-top: var(--base-layout-spacing-lg);
}

.feedback-meta {
  margin: 0.4rem 0 0;
  color: var(--color-text);
  opacity: 0.8;
  font-size: 0.9rem;
}

.edit-btn,
.primary-btn,
.secondary-btn {
  margin-top: var(--base-layout-spacing-lg);
  border-radius: 8px;
  padding: 10px 14px;
  border: 1px solid var(--color-border);
  cursor: pointer;
  font-weight: 600;
}

.edit-btn,
.primary-btn {
  background: var(--primary-gradient);
  color: var(--color-background);
}

.secondary-btn {
  background: transparent;
  color: var(--color-text);
}

.registration-modal-overlay {
  z-index: calc(var(--base-layout-modal-z-index) + 20);
  /* Override .page-section > * which caps width and shifts the overlay left */
  max-width: 100vw;
  margin-inline: 0;
}

.registration-modal-container {
  width: min(760px, 100%);
  max-width: min(760px, 100%); /* Override base .modal-container max-width: 600px */
  max-height: min(88vh, 900px);
  display: flex;
  flex-direction: column;
}

.registration-modal-body {
  overflow-y: auto;
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 1.5rem clamp(10px, 2.4vw, 24px);
}

.registration-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 1.25rem 1.5rem;
}

.details-form {
  background-color: transparent;
  border-radius: 0;
  padding: 0;
  width: 100%;
  max-width: 680px;
  margin-inline: auto;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(12px, 1.6vw, 20px);
  margin-bottom: clamp(14px, 1.8vw, 22px);
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field label {
  font-weight: var(--base-layout-form-label-weight);
  color: var(--color-heading);
  margin-bottom: var(--base-layout-form-label-margin-bottom);
  font-size: var(--base-layout-form-label-size);
}

.field-label--compact {
  display: block;
}

.form-field input,
.form-field select {
  padding: var(--base-layout-form-input-padding);
  border: var(--base-layout-form-input-border-width) solid var(--color-border);
  border-radius: var(--base-layout-form-input-border-radius);
  background-color: var(--color-background-mute);
  color: var(--color-text);
  font-size: var(--base-layout-form-input-font-size);
  text-align: center;
  transition: all var(--base-layout-transition-normal);
}

.form-field input::placeholder {
  text-align: center;
}

.form-field input:focus,
.form-field select:focus {
  outline: none;
  border-color: var(--vt-c-accent);
  box-shadow: 0 0 0 var(--base-layout-form-input-focus-ring-size) var(--base-layout-form-input-focus-ring-color);
  background-color: var(--color-background-secondary);
}

.form-field input.field-disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background-color: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.05);
}

.field-note {
  font-size: 0.75rem;
  color: var(--vt-c-accent);
  margin-top: 4px;
  opacity: 0.8;
}

.phone-field .phone-inputs {
  display: flex;
  flex-direction: column;
  gap: var(--base-layout-form-phone-input-gap);
  align-items: stretch;
  min-width: 0;
  width: 100%;
}

.phone-field .country-select-wrapper {
  width: 100%;
}

.phone-field .country-select {
  appearance: none;
  text-align: center;
  text-align-last: center;
  color: var(--color-text);
  background-color: var(--color-background-mute);
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.password-input input {
  width: 100%;
  padding-right: 2.75rem;
}

.toggle-visibility-btn {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--color-text);
  opacity: 0.7;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.toggle-visibility-btn:hover {
  opacity: 1;
}

.toggle-visibility-icon {
  width: 1.1rem;
  height: 1.1rem;
  fill: currentColor;
}

.hint {
  color: var(--color-text);
  opacity: 0.7;
  font-size: 0.8rem;
  margin-top: 6px;
  display: block;
}

.consent-row {
  display: flex;
  flex-direction: column;
  gap: var(--base-layout-consent-row-gap);
  margin: clamp(10px, 1.5vw, 18px) 0 0;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: var(--base-layout-checkbox-field-gap);
}

.checkbox-field input[type='checkbox'] {
  width: var(--base-layout-form-checkbox-size);
  height: var(--base-layout-form-checkbox-size);
  min-width: var(--base-layout-form-checkbox-size);
  min-height: var(--base-layout-form-checkbox-size);
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: var(--vt-c-accent);
}

.checkbox-field label {
  margin: 0;
  font-weight: normal;
  color: var(--color-text);
  font-size: 0.95rem;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-out;
  max-height: 200px;
  opacity: 1;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin: 0;
}

@media (max-width: 768px) {
  .user-details-section {
    padding-top: calc(var(--section-padding-block-start) + 3rem);
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .registration-modal-container {
    max-height: min(var(--mobile-modal-max-height), 92vh);
  }

  .registration-modal-body {
    padding: 0 12px calc(0.5rem + env(safe-area-inset-bottom, 0px));
  }

  .registration-modal-footer {
    padding: 1rem 12px calc(1rem + env(safe-area-inset-bottom, 0px));
  }

  .registration-feedback {
    padding: 24px;
  }
}
</style>
